import { trackUX, UX_EVENTS } from "@/lib/analytics/ux-events";
import { getCurrentSession, getUserId, setupAuthListener } from "@/lib/rpc";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, Outlet } from "react-router-dom";

import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { UnifiedDashboardSkeleton } from "@/components/skeletons";
import { useNavigate } from "react-router-dom";
import { useDashboardState } from "@/hooks/useDashboardState";
import { useProfileData } from "@/hooks/useProfileData";
import { useRealtimeUpdates } from "@/hooks/useRealtimeUpdates";
import { useKycRealtime } from "@/hooks/useKycRealtime";
import { getOnboarding } from "@/lib/rpc/user/onboarding";
import type { DashboardState } from "@/types/dashboard";
import type { DashboardContextType } from "@/components/dashboard/DashboardContext";
import { Text, Numbers } from "@/components/ui/typography";
import { semanticTokens } from '@packages/config/theme';
import { cn } from '@/lib/utils';

/**
 * Dashboard — Main user dashboard page
 * 
 * CANON 5.3/5.4: Single Loading State Architecture
 * 
 * SIMPLE APPROACH: Show ONE skeleton at TOP level until ALL data ready.
 * No cascading skeletons, no orchestrators, no complexity.
 * 
 * Rule: If ANY of these is true, show skeleton:
 * - No userId (auth not resolved)
 * - profileLoading (profile data loading)
 * - dashboardLoading (addresses, routes, transactions loading)
 * - onboardingLoading (onboarding mode loading)
 * 
 * When ALL ready → render DashboardLayout with children
 */
const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialUserId =
    typeof window !== 'undefined'
      ? window.localStorage.getItem('e2e_user_id') || ""
      : "";
  const [userId, setUserId] = useState<string>(initialUserId);
  const [statusRefreshKey, setStatusRefreshKey] = useState(0);
  const [onboardingMode, setOnboardingMode] = useState<'full' | 'receive_only' | null>(null);
  const [onboardingLoading, setOnboardingLoading] = useState(true);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  // P1-5: Track if initial load has completed to prevent skeleton on refresh
  const hasCompletedInitialLoadRef = useRef(false);

  // P0-1: Handle KYC pending state from VerificationStepScreen redirect
  const locationState = location.state as { kycPending?: boolean; kycStatus?: string } | null;
  const [kycPendingFromRedirect, setKycPendingFromRedirect] = useState(
    () => locationState?.kycPending ?? false
  );
  const [isKycRefreshing, setIsKycRefreshing] = useState(false);
  
  // Profile data management (loads independently)
  const {
    customerId,
    bridgeCustomerId,
    kycStatus: profileKycStatus,
    tosStatus: profileTosStatus,
    loading: profileLoading,
    refresh: refreshProfile
  } = useProfileData();

  const kycRealtime = useKycRealtime(customerId, {
    enabled: Boolean(customerId),
  });

  const kycStatus = kycRealtime.status ?? profileKycStatus;
  const tosStatus = kycRealtime.tosStatus ?? profileTosStatus;

  // Dashboard state management
  const { dashboardState, data, refresh, loading: dashboardLoading } = useDashboardState(customerId || '');

  // E2E overrides and derived state (calculated after hooks)
  const e2eModeOverride =
    typeof window !== 'undefined'
      ? window.localStorage.getItem('e2e_onboarding_mode')
      : null;
  const e2eDashboardStateOverride =
    typeof window !== 'undefined'
      ? window.localStorage.getItem('e2e_dashboard_state')
      : null;
  const normalizedE2eDashboardState =
    e2eDashboardStateOverride === 'ACTIVE' ||
      e2eDashboardStateOverride === 'EMPTY' ||
      e2eDashboardStateOverride === 'BANK_ADDED' ||
      e2eDashboardStateOverride === 'ADDRESS_CREATED'
      ? (e2eDashboardStateOverride as DashboardState)
      : null;
  const effectiveOnboardingMode =
    e2eModeOverride === 'full' || e2eModeOverride === 'receive_only'
      ? e2eModeOverride
      : onboardingMode;
  const effectiveDashboardState = normalizedE2eDashboardState ?? dashboardState;
  const e2eStubEnabled =
    typeof window !== 'undefined' && window.localStorage.getItem('e2e_dashboard_stub') === 'true';
  const stubMode = effectiveOnboardingMode ?? e2eModeOverride ?? 'receive_only';

  // Enable real-time updates when customer is identified
  useRealtimeUpdates();

  // Handle custom event for opening address page (backward compatibility)
  useEffect(() => {
    const handleOpenAddAddressModal = () => {
      navigate('/add-address', { state: { returnPath: '/dashboard' } });
    };

    window.addEventListener('open-add-address-modal', handleOpenAddAddressModal);
    return () => {
      window.removeEventListener('open-add-address-modal', handleOpenAddAddressModal);
    };
  }, [navigate]);

  const loadOnboardingMode = useCallback(async () => {
    setOnboardingLoading(true);
    try {
      if (typeof window !== 'undefined') {
        const e2eMode = window.localStorage.getItem('e2e_onboarding_mode');
        if (e2eMode === 'full' || e2eMode === 'receive_only') {
          if (window.localStorage.getItem('e2e_debug') === 'true') {
            console.log('[Dashboard] loadOnboardingMode (e2e bypass):', e2eMode);
          }
          setOnboardingMode(e2eMode);
          setOnboardingLoading(false);
          return;
        }
      }

      const snapshot = await getOnboarding();
      const mode =
        snapshot.onboarding_mode === 'full' || snapshot.onboarding_mode === 'receive_only'
          ? snapshot.onboarding_mode
          : null;

      setOnboardingMode(mode);
    } catch (error) {
      setOnboardingMode(null);
    } finally {
      setOnboardingLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Dashboard — Supabank";
    // Description
    const descContent = "User dashboard: profile, verification status, addresses.";
    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      document.head.appendChild(desc);
    }
    desc.setAttribute("content", descContent);

    // Canonical
    const href = `${location.origin}/dashboard`;
    let linkEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkEl) {
      linkEl = document.createElement("link");
      linkEl.setAttribute("rel", "canonical");
      document.head.appendChild(linkEl);
    }
    linkEl.setAttribute("href", href);
    void loadOnboardingMode();
  }, [loadOnboardingMode]);

  // Track dashboard_viewed event
  useEffect(() => {
    if (userId && !hasTrackedView) {
      // Determine entry point: post_onboarding if first_time=true, otherwise nav
      const searchParams = new URLSearchParams(location.search);
      const entry: 'post_onboarding' | 'nav' =
        searchParams.get('first_time') === 'true' ? 'post_onboarding' : 'nav';

      trackUX(UX_EVENTS.DASHBOARD_VIEWED, { entry });
      setHasTrackedView(true);
    }
  }, [userId, hasTrackedView, location.search]);

  useEffect(() => {
    // E2E Testing Bypass
    if (typeof window !== 'undefined') {
      const syntheticUserId = window.localStorage.getItem('e2e_user_id');
      if (syntheticUserId) {
        setUserId(syntheticUserId);
        return () => { };
      }

      if (window.localStorage.getItem('e2e_admin_bypass') === 'true') {
        setUserId('test-admin-user-id');
        return () => { };
      }
    }

    const cleanup = setupAuthListener((_event, session) => {
      setUserId(getUserId(session) || "");
    });

    // Initial session check
    getCurrentSession().then(session => {
      setUserId(getUserId(session) || "");
    });

    return cleanup;
  }, []);

  // ✅ RELAXED KYC CHECK: Allow 'incomplete' to see dashboard
  const kycLower = (kycStatus || "").toLowerCase();
  const verified = kycLower === "approved" || kycLower === "active" || kycLower === "incomplete";
  const tosLower = (tosStatus || "").toLowerCase();
  const tosAccepted = tosLower === "accepted" || tosLower === "approved";
  const canCreateAddress = verified && tosAccepted;

  const handleRefresh = () => {
    refresh();
    refreshProfile();
    setStatusRefreshKey(k => k + 1);
    void loadOnboardingMode();
  };

  // P0-1: Handler for KYC pending banner refresh
  const handleKycPendingRefresh = useCallback(async () => {
    setIsKycRefreshing(true);
    try {
      await refreshProfile();
      // Auto-dismiss if KYC is now approved
      const newKycStatus = (kycRealtime.status ?? profileKycStatus)?.toLowerCase();
      if (newKycStatus === 'approved' || newKycStatus === 'active') {
        setKycPendingFromRedirect(false);
      }
    } finally {
      setIsKycRefreshing(false);
    }
  }, [refreshProfile, kycRealtime.status, profileKycStatus]);

  // P0-1: Auto-dismiss KYC pending banner when realtime status updates to approved
  useEffect(() => {
    if (kycPendingFromRedirect) {
      const currentKycStatus = kycStatus?.toLowerCase();
      if (currentKycStatus === 'approved' || currentKycStatus === 'active') {
        setKycPendingFromRedirect(false);
      }
    }
  }, [kycStatus, kycPendingFromRedirect]);

  // E2E stub mode
  if (e2eStubEnabled) {
    return (
      <div className="min-h-screen bg-background p-6" data-testid="dashboard-e2e-stub">
        <div className="space-y-4 max-w-3xl mx-auto">
          <div>
            <Text variant="caption" color="muted" className="mb-1">E2E Dashboard Stub</Text>
            <Text as="span" data-testid="dashboard-mode-debug" variant="lg">
              {stubMode}
            </Text>
          </div>
          <Text data-testid="dashboard-text-debug" variant="md">
            {stubMode === 'full'
              ? 'You have full access to receive and withdraw funds.'
              : 'Withdrawals are disabled until you upgrade from Settings.'}
          </Text>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // CANON 5.3/5.4: SINGLE LOADING STATE
  //
  // Show ONE skeleton until ALL conditions met:
  // 1. userId exists (auth resolved)
  // 2. profileLoading = false
  // 3. dashboardLoading = false
  // 4. onboardingLoading = false
  //
  // This eliminates ALL cascading skeletons. Child components NEVER show
  // their own loading states on initial load — they receive ready data.
  //
  // P1-5 FIX: Only show skeleton on INITIAL load, not on refresh.
  // Once the user has seen the dashboard, we don't flash skeleton again.
  // Instead, child components handle their own refresh states (spinners, etc.)
  // ════════════════════════════════════════════════════════════════════════════

  const isDataLoading = !userId || profileLoading || dashboardLoading || onboardingLoading;

  // P1-5: Only show skeleton on initial load, not on refresh
  const shouldShowSkeleton = isDataLoading && !hasCompletedInitialLoadRef.current;

  // Mark initial load as complete when all data is ready
  if (!isDataLoading && !hasCompletedInitialLoadRef.current) {
    hasCompletedInitialLoadRef.current = true;
  }

  if (shouldShowSkeleton) {
    return (
      <div className="min-h-screen bg-background">
        <UnifiedDashboardSkeleton />
      </div>
    );
  }

  // All data ready — render dashboard with content
  const contextData: DashboardContextType = {
    userId,
    customerId,
    bridgeCustomerId,
    kycStatus,
    tosStatus,
    kycRealtime,
    canCreateAddress,
    addresses: Array.isArray(data.addresses) ? data.addresses : [],
    transactions: Array.isArray(data.transactions) ? data.transactions : [],
    payoutRoutes: Array.isArray(data.payoutRoutes) ? data.payoutRoutes : [],
    dashboardState: effectiveDashboardState,
    onAddressCreated: handleRefresh,
    onRefresh: handleRefresh,
    onStatusRefresh: () => setStatusRefreshKey(k => k + 1),
    // P1-5: Pass actual loading state so children can show spinners during refresh
    loading: isDataLoading,
    statusRefreshKey,
    onAddBankAccount: () => navigate('/add-bank-account', { state: { returnPath: '/dashboard' } }),
    onAddAddress: () => navigate('/add-address', { state: { returnPath: '/dashboard' } }),
    onboardingMode: effectiveOnboardingMode
  };

  return (
    <>
      {effectiveOnboardingMode && (
        <span data-testid="dashboard-mode-debug" className="sr-only">
          {effectiveOnboardingMode}
        </span>
      )}
      {effectiveDashboardState && (
        <span data-testid="dashboard-state-debug" className="sr-only">
          {effectiveDashboardState}
        </span>
      )}
      <DashboardLayout
        {...contextData}
        error={data.error || null}
        kycPending={kycPendingFromRedirect}
        onKycPendingDismiss={() => setKycPendingFromRedirect(false)}
        onKycPendingRefresh={handleKycPendingRefresh}
        isKycRefreshing={isKycRefreshing}
      >
        <Outlet context={contextData} />
      </DashboardLayout>
    </>
  );
};

export default Dashboard;
